import React, { useEffect, useState } from 'react';
import PageTitle from '../Components/PageTitle';
import AxiosApi from '../Helper/axios.api';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

export default function Maintenance() {
    const auth = useSelector((state) => state.auth);

    const [isLoading, setIsLoading] = useState(false);
    const [periods, setPeriods] = useState([]);
    const [form, setForm] = useState({
        start: '',
        end: '',
        note: '',
    });

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
    };

    const addMaintenance = async (e) => {
        e.preventDefault();
        if (!form.start || !form.end) {
            toast.error('Start and end time are required');
            return;
        }
        if (new Date(form.end) <= new Date(form.start)) {
            toast.error('End time must be after start time');
            return;
        }
        setIsLoading(true);
        try {
            const payload = {
                user_id: auth?.userDeatils?.user_id,
                start: form.start,
                end: form.end,
                note: form.note,
            };

            const response = await AxiosApi.withToken(
                auth.accessToken,
                'admin/add_maintenance',
                'post',
                payload
            );

            if (!response?.status) throw Error(response?.message || 'Failed to add');

            const newPeriod = {
                id: response?.data?.id || `${form.start}-${form.end}`,
                start: form.start,
                end: form.end,
                note: form.note,
            };
            setPeriods((prev) => [newPeriod, ...prev]);
            setForm({ start: '', end: '', note: '' });
            toast.success('Maintenance period added');
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error adding maintenance');
        } finally {
            setIsLoading(false);
        }
    };

    const fetchPeriods = async () => {
        setIsLoading(true);
        try {
            const res = await AxiosApi.withToken(
                auth.accessToken,
                'maintenance/status',
                'get'
            );
            if (!res || res.status === false) throw Error(res?.message || 'Failed to load');
            const d = res.data || {};
            let items = d.ongoing;
            items = items.concat(d.upcoming);
            const normalized = items
                .map((p) => ({
                    id: p.id,
                    start: p.start || p.start_time,
                    end: p.end || p.end_time,
                    note: p.note || p.message || '',
                }))
                .filter((p) => p.start && p.end)
                .sort((a, b) => new Date(a.start) - new Date(b.start));
            setPeriods(normalized);
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error loading maintenance periods');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchPeriods();
    }, []);

    const logoutAllUsers = async () => {
        if (!window.confirm('This will log out all users. Continue?')) return;
        setIsLoading(true);
        try {
            const payload = {
                user_id: auth?.userDeatils?.user_id,
            };
            const response = await AxiosApi.withToken(
                auth.accessToken,
                'admin/logout-users',
                'post',
                payload
            );
            if (!response?.status) throw Error(response?.message || 'Failed to log out users');
            toast.success(response?.message || 'All users have been logged out');
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error logging out users');
        } finally {
            setIsLoading(false);
        }
    };

    const removeMaintenance = async (period) => {
        setIsLoading(true);
        try {
            const payload = {
                user_id: auth?.userDeatils?.user_id,
                id: period.id,
            };
            const response = await AxiosApi.withToken(
                auth.accessToken,
                'admin/remove_maintenance',
                'post',
                payload
            );
            if (!response?.status) throw Error(response?.message || 'Failed to remove');
            setPeriods((prev) => prev.filter((p) => p.id !== period.id));
            toast.success('Maintenance period removed');
        } catch (error) {
            console.error(error);
            toast.error(error.message || 'Error removing maintenance');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <>
            <PageTitle title="DeepTherapy | Maintenance" />
            <div className="virtualization">
                <div className="card w-100">
                    <div className="card-body">
                        <div className="d-flex justify-content-center mb-4">
                            <button
                                type="button"
                                className="btn btn-warning btn-lg"
                                disabled={isLoading}
                                onClick={logoutAllUsers}
                            >
                                Log out all users
                            </button>
                        </div>
                        <h5 className="mb-3">Add Maintenance Period</h5>
                        <form onSubmit={addMaintenance} className="mb-4">
                            <div className="row g-2 align-items-end">
                                <div className="col-md-3">
                                    <label className="form-label">Start Time</label>
                                    <input
                                        type="datetime-local"
                                        name="start"
                                        className="form-control"
                                        value={form.start}
                                        onChange={onChange}
                                        disabled={isLoading}
                                        required
                                    />
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">End Time</label>
                                    <input
                                        type="datetime-local"
                                        name="end"
                                        className="form-control"
                                        value={form.end}
                                        onChange={onChange}
                                        disabled={isLoading}
                                        required
                                    />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label">Note (optional)</label>
                                    <input
                                        type="text"
                                        name="note"
                                        className="form-control"
                                        value={form.note}
                                        onChange={onChange}
                                        disabled={isLoading}
                                        placeholder="Displayed to users during maintenance"
                                    />
                                </div>
                                <div className="col-md-2">
                                    <button type="submit" className="btn btn-primary w-100" disabled={isLoading}>
                                        {isLoading ? 'Saving…' : 'Add'}
                                    </button>
                                </div>
                            </div>
                        </form>

                        <h5 className="mb-2">Scheduled Maintenance Periods</h5>
                        {periods.length === 0 ? (
                            <div className="text-muted">No maintenance periods scheduled.</div>
                        ) : (
                            <table className="table">
                                <thead>
                                    <tr style={{ backgroundColor: '#f1f1f1' }}>
                                        {['Start', 'End', 'Note', 'Action'].map((header, index) => (
                                            <th key={index} className="p-2 text-center">
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody style={{ opacity: isLoading ? 0.5 : 1 }}>
                                    {periods.map((p) => (
                                        <tr key={p.id}>
                                            <td className="p-2 text-center">{new Date(p.start).toLocaleString()}</td>
                                            <td className="p-2 text-center">{new Date(p.end).toLocaleString()}</td>
                                            <td className="p-2 text-center">{p.note || '-'}</td>
                                            <td className="p-2 text-center">
                                                <button
                                                    className="btn btn-outline-danger btn-sm"
                                                    disabled={isLoading}
                                                    onClick={() => removeMaintenance(p)}
                                                >
                                                    Remove
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
}
